import { useMediaQuery } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalViews } from '../../constants';
import { useAsyncAction } from '../../hooks';
import { usePrevious } from '../../hooks/usePrevious';
import {
  AccessRequestDetails,
  AccessRequestItem,
  RequestStatusType,
  useUserPermissions,
} from '../../hooks/useUserPermissions';
import { Icons } from '../../stories/atoms';
import { ColumnFlexContainer, ContainerV2 } from '../../stories/atoms/containerV2/ContainerV2';
import { Animation, Animations } from '../../stories/molecules';
import { Page } from '../../stories/organisms';
import { muiTheme } from '../../theme';
import { commonTranslations } from '../../translations';
import { accessRequestTranslations } from '../../translations/accessRequest';
import { isFailureResponse } from '../../utils';
import { usePageEventTracker } from '../../hooks/usePageEventTracker';
import { AccessApprovalTable } from './accessApprovalTable/AccessApprovalTable';
import { ManageAccessModal } from './manageAccessModal/ManageAccessModal';
import { StatusCard } from './statusCard/StatusCard';
import { countItemsExpiringWithinDays, getDaysLeftForAccessExpiry } from './util';

export const AccessApprovalPage: FC<unknown> = observer(() => {
  usePageEventTracker('access_approvals');

  const { t } = useTranslation();

  const { errorText2 } = commonTranslations;
  const {
    loading,
    error,
    noData,
    totalActions,
    pendingActions,
    expiringIn5DaysActions,
    approvedActions,
    rejectedActions,
    expiredActions,
  } = accessRequestTranslations;

  const isTablet = useMediaQuery(muiTheme.breakpoints.down('desktop'));

  const { fetchAccessRequest, updateAccessRequest } = useUserPermissions();

  const [isManageAccessModalOpen, setIsManageAccessModalOpen] = useState(false);
  // TODO: Uncomment this when the expired action is implemented [BTCOBUI-3288]
  // const [isTriggerNotificationModalOpen, setIsTriggerNotificationModalOpen] = useState(false);
  const [requestAction, setRequestAction] = useState<RequestStatusType | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<AccessRequestItem | null>(null);
  const [accessRequestData, setAccessRequestData] = useState<AccessRequestDetails | null>(null);
  const [modalView, setModalView] = useState<ModalViews>(ModalViews.INITIAL);
  const [activeFilter, setActiveFilter] = useState<
    'total' | 'pending' | 'expiring' | 'approved' | 'rejected' | 'expired' | null
  >(null);
  const [statusCardDetails, setStatusCardDetails] = useState({
    totalActions: 0,
    pendingActions: 0,
    expiringIn5DaysActions: 0,
    approvedActions: 0,
    rejectedActions: 0,
    expiredActions: 0,
  });

  // Fetch access request data
  const fetchData = async () => {
    const response = await fetchAccessRequest();

    if (isFailureResponse(response)) {
      throw new Error();
    }
    return response.data;
  };

  // Update access request
  const updateData = async (requestID: string, updatedAction: RequestStatusType) => {
    const response = await updateAccessRequest(requestID, updatedAction);

    if (isFailureResponse(response)) {
      setModalView(ModalViews.ERROR);
      throw new Error();
    }
    setModalView(ModalViews.SUCCESS);

    return response.data;
  };

  const {
    result: accessRequestDetails,
    isLoading: accessRequestLoading,
    isError: accessRequestError,
    triggerAction: triggerFetchAccessRequest,
  } = useAsyncAction<AccessRequestDetails>();

  const {
    result: updatedAccessRequest,
    isLoading: updateAccessRequestLoading,
    triggerAction: triggerUpdateAccessRequest,
  } = useAsyncAction<AccessRequestItem>();

  const previousUpdatedRequest = usePrevious(updatedAccessRequest);

  useEffect(() => {
    triggerFetchAccessRequest(fetchData);
  }, []);

  useEffect(() => {
    if (!accessRequestDetails) return;
    setAccessRequestData(accessRequestDetails);
  }, [accessRequestDetails]);

  useEffect(() => {
    if (updatedAccessRequest === previousUpdatedRequest) return;
    if (!updatedAccessRequest || !accessRequestData) return;

    const updatedRequestItems = accessRequestData.requests.map((request) => {
      if (request.id === updatedAccessRequest.id) {
        return updatedAccessRequest;
      }
      return request;
    });

    setAccessRequestData({
      requests: updatedRequestItems,
    });
  }, [accessRequestData, updatedAccessRequest, previousUpdatedRequest]);

  useEffect(() => {
    if (!accessRequestData) return;
    setStatusCardDetails((prev) => ({
      ...prev,
      totalActions: accessRequestData.requests.length,
      pendingActions: accessRequestData.requests.filter((request) => request.status === 'Pending').length,
      rejectedActions: accessRequestData.requests.filter((request) => request.status === 'Rejected').length,
      expiredActions: countItemsExpiringWithinDays(accessRequestData.requests, 0),
      expiringIn5DaysActions: countItemsExpiringWithinDays(accessRequestData.requests, 5),
      approvedActions: accessRequestData.requests.filter((request) => request.status === 'Approved').length,
    }));
  }, [accessRequestData]);

  useEffect(() => {
    if (!isManageAccessModalOpen) {
      setModalView(ModalViews.INITIAL);
    }
  }, [isManageAccessModalOpen]);

  const handleModalAction = async (requestId: string, updatedStatus: RequestStatusType) => {
    triggerUpdateAccessRequest(() => updateData(requestId, updatedStatus));
  };

  const handleFilterClick = (filter: typeof activeFilter) => {
    setActiveFilter((prev) => (prev === filter ? null : filter));
  };

  const filteredRequests = useMemo(
    () =>
      (accessRequestData?.requests ?? []).filter((request) => {
        if (!activeFilter || activeFilter === 'total') return true;
        if (activeFilter === 'pending') return request.status === 'Pending';
        if (activeFilter === 'approved')
          return request.status === 'Approved' && getDaysLeftForAccessExpiry(request.statusTimestamp, 365) > 0;
        if (activeFilter === 'rejected') return request.status === 'Rejected';
        if (activeFilter === 'expiring') {
          const daysLeft = getDaysLeftForAccessExpiry(request.statusTimestamp, 365);
          return request.status === 'Approved' && daysLeft <= 5 && daysLeft > 0;
        }
        if (activeFilter === 'expired')
          return request.status === 'Approved' && getDaysLeftForAccessExpiry(request.statusTimestamp, 365) <= 0;
        return true;
      }),
    [accessRequestData, activeFilter],
  );

  // Pending (in review) first, then the rest sorted by most recent timestamp
  const sortedFilteredRequests = useMemo(
    () =>
      [...filteredRequests].sort((a, b) => {
        if (a.status === 'Pending' && b.status !== 'Pending') return -1;
        if (a.status !== 'Pending' && b.status === 'Pending') return 1;
        const timestampA = a.status === 'Pending' ? a.requestTimestamp : a.statusTimestamp;
        const timestampB = b.status === 'Pending' ? b.requestTimestamp : b.statusTimestamp;
        return new Date(timestampB).getTime() - new Date(timestampA).getTime();
      }),
    [filteredRequests],
  );

  const handleTableAction = (action: RequestStatusType, requestId: string) => {
    const selectedAccessRequest = accessRequestData?.requests.find((request) => request.id === requestId);
    if (selectedAccessRequest && action === 'Pending') {
      setRequestAction(action);
      setSelectedRequest(selectedAccessRequest);
      setIsManageAccessModalOpen(true);
    }

    // TODO: Uncomment this when the expired action is implemented [BTCOBUI-3288]
    // if (selectedAccessRequest && action === 'expired') {
    //   setRequestAction(action);
    //   setSelectedRequest(selectedAccessRequest);
    //   setIsTriggerNotificationModalOpen(true);
    // }
  };

  return (
    <Page
      body={
        <>
          {selectedRequest && requestAction && isManageAccessModalOpen && (
            <ManageAccessModal
              isOpen={isManageAccessModalOpen}
              requestAction={requestAction}
              selectedItem={selectedRequest}
              isLoading={updateAccessRequestLoading}
              modalView={modalView}
              onClose={() => setIsManageAccessModalOpen(false)}
              sendRequest={handleModalAction}
            />
          )}

          {/* TODO: Uncomment this when the expired action is implemented [BTCOBUI-3288] */}
          {/* {selectedRequest && requestAction && isTriggerNotificationModalOpen && (
            <TriggerNotificationModal
              isOpen={isTriggerNotificationModalOpen}
              selectedItem={selectedRequest}
              onClose={() => setIsTriggerNotificationModalOpen(false)}
            />
          )} */}

          {accessRequestLoading && (
            <Animation title={t(loading.key, loading.defaultValue)} animation={Animations.LOADING} />
          )}

          {!accessRequestLoading && accessRequestError && (
            <Animation
              title={t(errorText2.key, errorText2.defaultValue)}
              subtitle={t(error.key, error.defaultValue)}
              animation={Animations.SAD}
            />
          )}

          {!accessRequestLoading && !accessRequestError && !accessRequestData?.requests.length && (
            <Animation title={t(noData.key, noData.defaultValue)} animation={Animations.CONFUSED} />
          )}

          {!accessRequestLoading && !accessRequestError && !!accessRequestData?.requests.length && (
            <ColumnFlexContainer padding={isTablet ? [3, 4] : [6, 8]} data-testid="access-request-page-wrapper">
              <ContainerV2
                display="grid"
                gap={[3]}
                style={{ gridTemplateColumns: isTablet ? 'repeat(3, 1fr)' : 'repeat(6, 1fr)' }}
                data-testid="status-card-wrapper"
              >
                <StatusCard
                  title={t(totalActions.key, totalActions.defaultValue)}
                  icon={Icons.HOURLASS}
                  value={statusCardDetails.totalActions}
                  isActive={activeFilter === 'total' || activeFilter === null}
                  onClick={() => handleFilterClick('total')}
                />
                <StatusCard
                  title={t(pendingActions.key, pendingActions.defaultValue)}
                  icon={Icons.SEARCH}
                  value={statusCardDetails.pendingActions}
                  isActive={activeFilter === 'pending'}
                  onClick={() => handleFilterClick('pending')}
                />
                <StatusCard
                  title={t(expiringIn5DaysActions.key, expiringIn5DaysActions.defaultValue)}
                  icon={Icons.WARNING}
                  value={statusCardDetails.expiringIn5DaysActions}
                  iconColor="red"
                  isActive={activeFilter === 'expiring'}
                  onClick={() => handleFilterClick('expiring')}
                />
                <StatusCard
                  title={t(approvedActions.key, approvedActions.defaultValue)}
                  icon={Icons.CHECK_MARK_CIRCLE}
                  value={statusCardDetails.approvedActions}
                  iconColor="validationGreen"
                  isActive={activeFilter === 'approved'}
                  onClick={() => handleFilterClick('approved')}
                />
                <StatusCard
                  title={t(rejectedActions.key, rejectedActions.defaultValue)}
                  icon={Icons.CLOSEROUND}
                  value={statusCardDetails.rejectedActions}
                  isActive={activeFilter === 'rejected'}
                  onClick={() => handleFilterClick('rejected')}
                />
                <StatusCard
                  title={t(expiredActions.key, expiredActions.defaultValue)}
                  icon={Icons.CLOCK}
                  value={statusCardDetails.expiredActions}
                  isActive={activeFilter === 'expired'}
                  onClick={() => handleFilterClick('expired')}
                />
              </ContainerV2>
              <AccessApprovalTable requestItems={sortedFilteredRequests} sendTableAction={handleTableAction} />
            </ColumnFlexContainer>
          )}
        </>
      }
    />
  );
});
